/**
 * Shared alarm grouping/filtering for Reports preview and CSV export.
 */

export type GroupedAlarmEvent = {
  event_id?: string;
  idAlarmLog?: number;
  sensor_id: number;
  activated: string;
  alarm_reset: string | null;
  alarm_type: string;
  alarm_values: number | null;
  acknowledged: boolean;
  acknowledged_by: string | null;
  notes?: string | null;
};

export function groupAlarmLogEvents(alarmLog: unknown[]): GroupedAlarmEvent[] {
  if (!Array.isArray(alarmLog)) return [];
  const map = new Map<string, any[]>();
  for (const log of alarmLog) {
    const id = log.event_id || `log-${log.idAlarmLog}`;
    if (!map.has(id)) map.set(id, []);
    map.get(id)!.push(log);
  }
  return Array.from(map.values()).map(group => {
    group.sort((a, b) => new Date(a.activated).getTime() - new Date(b.activated).getTime());
    const initial = group[0];
    const reset = group.find((l: any) => l.alarm_reset);
    const isAck = group.every((l: any) => l.acknowledged);
    const lastAck = [...group].reverse().find((l: any) => l.acknowledged_by);
    const numValues = group.map((l: any) => Number(l.alarm_values)).filter(n => !isNaN(n));
    const worstValue =
      numValues.length === 0
        ? null
        : initial.alarm_type === 'high'
          ? Math.max(...numValues)
          : Math.min(...numValues);
    return {
      ...initial,
      alarm_reset: reset ? reset.alarm_reset : null,
      alarm_values: worstValue,
      acknowledged: isAck,
      acknowledged_by: lastAck?.acknowledged_by ?? null,
    };
  });
}

export function filterGroupedAlarms(
  events: GroupedAlarmEvent[],
  opts: {
    startDate: string;
    endDate: string;
    onlyUnacknowledged: boolean;
    /** Empty or undefined = include all sensors */
    sensorIds?: number[] | undefined;
  }
): GroupedAlarmEvent[] {
  const start = new Date(opts.startDate).getTime();
  const end = new Date(opts.endDate).getTime();
  const idSet =
    opts.sensorIds && opts.sensorIds.length > 0 ? new Set(opts.sensorIds) : null;

  return events
    .filter(e => {
      if (!e.activated) return false;
      const t = new Date(e.activated).getTime();
      if (t < start || t > end) return false;
      if (opts.onlyUnacknowledged && e.acknowledged) return false;
      if (idSet && !idSet.has(e.sensor_id)) return false;
      return true;
    })
    .sort((a, b) => new Date(b.activated).getTime() - new Date(a.activated).getTime());
}

export function estimateBucketCountMs(startIso: string, endIso: string, bucketMinutes: number): number {
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || bucketMinutes <= 0) return 0;
  const span = Math.max(0, end - start);
  return Math.ceil(span / (bucketMinutes * 60 * 1000));
}
